import React, { useCallback, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog';
import PaginationFooter from '../../../components/PaginationFooter';
import BlockedItemsAlert from './BlockedItemsAlert';
import CatalogBulkBar from './CatalogBulkBar';
import CatalogFilters from './CatalogFilters';
import CatalogTable from './CatalogTable';
import ItemEditDialog from './ItemEditDialog';
import ItemPreviewDialog from './ItemPreviewDialog';
import useCatalogItems from '../../hooks/useCatalogItems';
import useItemStatusChange from '../../hooks/useItemStatusChange';

/**
 * Aba Catálogo: todas as novidades (rascunho, publicadas, internas e descartadas), com busca, filtros,
 * ações em lote de status e o editor de cada novidade.
 * @param {{ notify: Function }} props
 */
function CatalogTab({ notify }) {
  const catalog = useCatalogItems();
  const { reload, replaceItem } = catalog;
  // Seleção vale entre páginas: id -> item
  const [selected, setSelected] = useState(() => new Map());
  // undefined = fechado; null = nova; objeto = editar
  const [editing, setEditing] = useState(undefined);
  const [previewing, setPreviewing] = useState(null);
  // Itens a descartar, aguardando confirmação
  const [discarding, setDiscarding] = useState([]);

  const clearSelection = useCallback(() => setSelected(new Map()), []);
  const afterStatus = useCallback(() => { clearSelection(); reload(); }, [clearSelection, reload]);
  const status = useItemStatusChange({ notify, onDone: afterStatus });

  const toggle = useCallback((item) => setSelected((prev) => {
    const next = new Map(prev);
    if (next.has(item.id)) next.delete(item.id);
    else next.set(item.id, item);
    return next;
  }), []);

  const togglePage = useCallback((items, select) => setSelected((prev) => {
    const next = new Map(prev);
    items.forEach((item) => (select ? next.set(item.id, item) : next.delete(item.id)));
    return next;
  }), []);

  const handleSaved = (item) => {
    const isNew = editing === null;
    setEditing(undefined);
    notify(isNew ? 'Novidade criada como rascunho.' : 'Novidade salva.', 'success');
    if (isNew) reload();
    else replaceItem(item);
  };

  // Mudar o status já recarrega a lista e avisa (ou mostra o bloqueio, ex.: falta a área)
  const handleSavedAndReviewed = (item) => {
    setEditing(undefined);
    status.changeStatus([item], 'reviewed');
  };

  const handleChangeStatus = (newStatus, notifyBell) => {
    const items = Array.from(selected.values());
    if (newStatus === 'discarded') setDiscarding(items);
    else status.changeStatus(items, newStatus, notifyBell);
  };

  const handleDiscardFromEditor = (item) => {
    setEditing(undefined);
    setDiscarding([item]);
  };

  const confirmDiscard = () => {
    status.changeStatus(discarding, 'discarded');
    setDiscarding([]);
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Revisado é o que está pronto para entrar num envio. Publicar não envia e-mail. Itens aguardando deploy não podem ser publicados nem enviados.
        </Typography>
        <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => setEditing(null)}>
          Nova novidade
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        <CatalogFilters
          filters={catalog.filters}
          onFilterChange={catalog.setFilter}
          searchInput={catalog.searchInput}
          onSearchInputChange={catalog.setSearchInput}
        />
        <CatalogBulkBar
          count={selected.size}
          busy={status.isChanging}
          onClear={clearSelection}
          onChangeStatus={handleChangeStatus}
        />
        <BlockedItemsAlert blocked={status.blocked} onClose={status.clearBlocked} />
        {catalog.error && <Alert severity="error" sx={{ borderRadius: 0 }}>{catalog.error}</Alert>}
        <CatalogTable
          items={catalog.items}
          isLoading={catalog.isLoading}
          hasActiveFilters={catalog.hasActiveFilters}
          selectedIds={selected}
          onToggle={toggle}
          onTogglePage={togglePage}
          onEdit={setEditing}
          onPreview={setPreviewing}
        />
        <PaginationFooter pagination={catalog.pagination} onPageChange={catalog.setPage} itemLabel="novidades" />
      </Paper>

      <ItemEditDialog
        open={editing !== undefined}
        item={editing || null}
        onClose={() => setEditing(undefined)}
        onSaved={handleSaved}
        onSavedAndReviewed={handleSavedAndReviewed}
        onDiscard={handleDiscardFromEditor}
      />
      <ItemPreviewDialog item={previewing} onClose={() => setPreviewing(null)} />
      <ConfirmDialog
        isOpen={discarding.length > 0}
        title={discarding.length === 1 ? 'Descartar novidade?' : `Descartar ${discarding.length} novidades?`}
        message="Some do catálogo e nunca entra em envio. O /gabriela-app-changelog não recria. Para desfazer, filtre por Descartado e volte a rascunho."
        confirmLabel="Descartar"
        variant="danger"
        onConfirm={confirmDiscard}
        onCancel={() => setDiscarding([])}
      />
    </>
  );
}

export default CatalogTab;
